import { hash } from 'ohash'

import type { AnswerValue } from './contract'
import type {
  Citation,
  CodeList,
  DesignMeta,
  ExternalDoc,
  NotAskedEntry,
  PolicyDataset,
  PolicyDocView,
  PolicyInsight,
  PolicyMetadata,
  PolicySource,
  PredicateData,
  PulledIn,
} from './types'

/* A definition is everything that determines what the policy decides.
   Layout and `nodeLinks` are deliberately excluded: dragging a node is not a
   new version, and tracing a node to a field does not change the tree's
   logic. Versions are content-addressed, so reverting to an earlier
   definition reuses its version number. */

export interface DefinitionSnapshot {
  answers: Record<string, AnswerValue>
  notAsked: Record<string, NotAskedEntry>
  pulledIn: Record<string, PulledIn>
  citations: Citation[]
  sources: PolicySource[]
  description: string
  nodes: PredicateData[]
  insights: PolicyInsight[]
  metadata: PolicyMetadata[]
  datasets: PolicyDataset[]
  externalDocs: ExternalDoc[]
  designMeta: DesignMeta
  codeLists: Record<string, CodeList>
}

/** The fields a scoping approval is pinned to: everything scoping can edit. */
export const SCOPE_KEYS = [
  'answers',
  'notAsked',
  'pulledIn',
  'citations',
  'sources',
  'description',
] as const satisfies readonly (keyof DefinitionSnapshot)[]

export const definitionSnapshot = (
  view: Pick<PolicyDocView, keyof DefinitionSnapshot>,
): DefinitionSnapshot => ({
  answers: view.answers,
  notAsked: view.notAsked,
  pulledIn: view.pulledIn,
  citations: view.citations,
  sources: view.sources,
  description: view.description,
  nodes: view.nodes,
  insights: view.insights,
  metadata: view.metadata,
  datasets: view.datasets,
  externalDocs: view.externalDocs,
  designMeta: view.designMeta,
  codeLists: view.codeLists,
})

/** Hash of the whole definition: scope plus design. Changes whenever anything
 *  a design or validation approval is pinned to changes. */
export const definitionFingerprint = (
  view: Pick<PolicyDocView, keyof DefinitionSnapshot>,
): string => hash(definitionSnapshot(view))

/** Hash of only the scope slice of the definition. Changes whenever anything
 *  a scoping approval is pinned to changes, and nothing else. */
export const scopeFingerprint = (
  view: Pick<PolicyDocView, (typeof SCOPE_KEYS)[number]>,
): string =>
  hash({
    answers: view.answers,
    notAsked: view.notAsked,
    pulledIn: view.pulledIn,
    citations: view.citations,
    sources: view.sources,
    description: view.description,
  })

export interface PolicyVersion {
  seq: number
  fingerprint: string
  scopeFingerprint: string
  at: string
  actor: string
}
